import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ApiService, AppDispatch, RootState, navigate } from "src/common";
import { AppRoutes } from "src/pages/app";

// ============ INTERFACE ============

/** Stored health questionnaire answers per offer (key = productCode). */
export interface HealthQuestionnaireStoredState {
  seriousDiseaseValue: string;
  selectedDiseases: string[];
  isConfirmed: boolean;
}

export interface OfferStateInterface {
  data?: any;
  offerId?: string;
  /** Set to ProductTypeCodeEnum.DODATNO for additional flow; undefined/DOPUNSKO for main flow */
  productTypeCode?: string;
  oneTimeToggle?: boolean;
  oneTimeOffersToggle?: boolean;
  bListToggle?: boolean;
  /** Selections for editOnWeb coverages (e.g. eCard). Used for query refetch and Kupi. Label/description for summary come from checkout (supplementary) config. */
  editableCoverageSelections?: Record<string, boolean>;
  soId?: string;
  soIdWeb?: string;
  paymentType?: string;
  formValues?: { [key: string]: any };
  isContractorSameAsInsuredPerson?: boolean;
  selectedProductForAdditional?: any;
  /** Additional flow: questionnaire answers by productCode for restore when returning from potvrda. */
  healthQuestionnaireByProductCode?: Record<string, HealthQuestionnaireStoredState>;
}

// ============ INIT STATE ============

const initialState: OfferStateInterface = {
};

// ========= SLICE ==========

export const OfferSlice = createSlice({
  name: 'offer',
  initialState,
  reducers: {
    setOfferId: (state: OfferStateInterface, action: PayloadAction<any>) => {
      state.offerId = action.payload
    },
    setProductTypeCode: (state: OfferStateInterface, action: PayloadAction<string | undefined>) => {
      state.productTypeCode = action.payload
    },
    setSoId: (state: OfferStateInterface, action: PayloadAction<any>) => {
      state.soId = action.payload
    },
    setSoIdWeb: (state: OfferStateInterface, action: PayloadAction<any>) => {
      state.soIdWeb = action.payload
    },
    setPaymentType: (state: OfferStateInterface, action: PayloadAction<any>) => {
      state.paymentType = action.payload
    },
    setOneTimeToggle: (state: OfferStateInterface, action: PayloadAction<boolean>) => {
      state.oneTimeToggle = action.payload
    },
    setOneTimeOffersToggle: (state: OfferStateInterface, action: PayloadAction<boolean>) => {
      state.oneTimeOffersToggle = action.payload
    },
    setBListToggle: (state: OfferStateInterface, action: PayloadAction<boolean>) => {
      state.bListToggle = action.payload
    },
    setEditableCoverageSelection: (state: OfferStateInterface, action: PayloadAction<{ code: string; value: boolean }>) => {
      if (!state.editableCoverageSelections) state.editableCoverageSelections = {};
      state.editableCoverageSelections[action.payload.code] = action.payload.value;
    },
    setFormState: (state: OfferStateInterface, action: PayloadAction<{ reduxName: string, formValues: any }>) => {
      const { reduxName, formValues } = action.payload;
      if (!state.formValues) {
        state.formValues = {};
      }
      state.formValues[reduxName] = formValues;
    },
    updateFormState: (state: OfferStateInterface, action: PayloadAction<{ reduxName: string, formValues: any }>) => {
      const { reduxName, formValues } = action.payload;
      if (!state.formValues) {
        state.formValues = {};
      }
      if (!state.formValues[reduxName]) {
        state.formValues[reduxName] = {};
      }
      
      // Spoji postojeće vrijednosti s novim
      state.formValues[reduxName] = {
        ...state.formValues[reduxName],
        ...formValues
      };
    },
    resetFormState: (state: OfferStateInterface) => {
      state.formValues = undefined;
      state.isContractorSameAsInsuredPerson = true;
    },
    setIsContractorSameAsInsuredPersonStore: (state: OfferStateInterface, action: PayloadAction<boolean>) => {
      state.isContractorSameAsInsuredPerson = action.payload
    },
    setSelectedProductForAdditional: (state: OfferStateInterface, action: PayloadAction<any>) => {
      state.selectedProductForAdditional = action.payload;
    },
    setHealthQuestionnaireForOffer: (state: OfferStateInterface, action: PayloadAction<{ productCode: string; data: HealthQuestionnaireStoredState }>) => {
      const { productCode, data } = action.payload
      if (!state.healthQuestionnaireByProductCode) {
        state.healthQuestionnaireByProductCode = {}
      }
      state.healthQuestionnaireByProductCode[productCode] = data
    },
  }
})

export const { setOfferId, setProductTypeCode, setOneTimeToggle, setBListToggle, setOneTimeOffersToggle, setEditableCoverageSelection, setSoId, setSoIdWeb, setPaymentType, setFormState, updateFormState, resetFormState, setIsContractorSameAsInsuredPersonStore, setSelectedProductForAdditional, setHealthQuestionnaireForOffer } = OfferSlice.actions;

export default OfferSlice.reducer;

export const createOffer = (productCode: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  const customer = getState().model.customer.data;
  ApiService.createOffer(productCode, customer).subscribe({
    next: (response: any) => {
      const urlParams = new URLSearchParams(new URL(response.data.checkoutUrl).search);
      const offerId = urlParams.get('OfferID');
      dispatch(setOfferId(offerId));
      navigate(`${AppRoutes.OFFER.fullPath}/${offerId}`);
    },
    error: () => {
      console.log('Error while creating offer')
    },
  });
};
